import { wsUrl } from '@web/api/client'
import type { Command, FrameRecord } from '@web/api/types'

export type SocketStatus = 'connecting' | 'open' | 'closed'

/**
 * Reconnecting session socket. Frames arrive in `seq` order; on reconnect the
 * server replays everything after the last seq we saw, so the consumer sees a
 * ordered, deduplicated stream whose durable positions may have gaps.
 */
export class SessionSocket {
  private ws: WebSocket | null = null
  private lastSeq = 0
  private closed = false
  private hydrating = true
  private batched = false
  private retry = 0
  private timer: number | null = null

  constructor(
    private sessionId: string,
    private onFrame: (rec: FrameRecord) => void,
    private onStatus: (s: SocketStatus) => void,
    after = 0,
  ) {
    this.lastSeq = after
    this.connect()
  }

  private connect() {
    if (this.closed) return
    this.hydrating = true
    this.batched = false
    this.onStatus('connecting')
    const ws = new WebSocket(
      wsUrl(
        `/v1/sessions/${this.sessionId}/ws?after=${this.lastSeq}&history=batch-v2`,
      ),
    )
    this.ws = ws
    ws.onopen = () => {
      if (this.closed || this.ws !== ws) return
      this.retry = 0
      this.onStatus('open')
    }
    ws.onmessage = (ev) => {
      if (this.closed || this.ws !== ws) return
      const rec = JSON.parse(ev.data as string) as FrameRecord
      if (rec.seq > 0 && rec.seq <= this.lastSeq) return
      if (rec.frame.type === 'bridge/history_begin') this.batched = true
      this.onFrame(rec)
      // Advance only after the consumer accepted the entire envelope. Cursors
      // are API positions and may have gaps; they are never runner positions.
      if (rec.frame.type === 'bridge/history_batch') {
        for (const item of rec.frame.records as FrameRecord[])
          this.lastSeq = Math.max(this.lastSeq, item.seq)
      } else if (rec.frame.type === 'bridge/history_complete') {
        this.lastSeq = Math.max(this.lastSeq, Number(rec.frame.through))
        this.hydrating = false
      } else if (rec.frame.type === 'bridge/hello' && !this.batched) {
        this.hydrating = false
      }
      if (rec.seq > this.lastSeq) this.lastSeq = rec.seq
    }
    ws.onclose = (ev) => {
      if (this.closed || this.ws !== ws) return
      this.ws = null
      this.onStatus('closed')
      if (ev.code === 4401 || ev.code === 4403) {
        window.dispatchEvent(new Event('bridge:identity-expired'))
        return
      }
      if (this.closed || ev.code === 4404) return
      const delay = Math.min(1000 * 2 ** this.retry++, 10_000)
      this.timer = window.setTimeout(() => this.connect(), delay)
    }
    ws.onerror = () => {
      /* onclose follows */
    }
  }

  send(cmd: Command): boolean {
    if (this.hydrating || !this.ws || this.ws.readyState !== WebSocket.OPEN)
      return false
    this.ws.send(JSON.stringify(cmd))
    return true
  }

  close() {
    this.closed = true
    if (this.timer) window.clearTimeout(this.timer)
    this.ws?.close()
    this.ws = null
  }
}
